"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiPut } from "@/lib/api-client";
import type {
  AdminUser,
  PendingValidationUser,
  FamilyGroup,
  Reservation,
} from "@/lib/api-client";
import { useAppStore, currentView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/inline-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Inbox,
  ShieldCheck,
  UserCheck,
  Users,
  Users as UsersIcon,
  Users2,
  Mail,
  Home,
  Copy,
  Check,
  X,
  Loader2,
  Power,
} from "lucide-react";
import { isStaffRole, isOwnerAdmin, formatDate  } from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";
import { GroupsPanel } from "@/components/booking/groups-panel";
import { InvitationsPanel } from "@/components/booking/invitations-panel";

export function ModeratorView() {
  const user = useAppStore((s) => s.user);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const ownerAdmin = isOwnerAdmin(user?.role);
  const permissions = useAppStore((s) => s.permissions);
  const canValidateBookings = ownerAdmin || permissions.includes("bookings.validate");
  const [tab, setTab] = useState(
    ownerAdmin || canValidateBookings ? "review" : "invitations"
  );

  // Pending bookings badge (same query key as the Booking requests panel —
  // React Query dedupes the fetch).
  const { data: resData } = useQuery({
    queryKey: ["reservations"],
    queryFn: () => apiGet<{ reservations: Reservation[] }>("/api/reservations"),
    enabled: canValidateBookings,
  });
  const pendingBookings = (resData?.reservations ?? []).filter(
    (r) => r.status === "PENDING"
  ).length;

  const { data, isLoading } = useQuery({
    queryKey: ["pending-users"],
    queryFn: () => apiGet<{ users: PendingValidationUser[] }>("/api/admin/validate-users"),
    enabled: !!user && (isStaffRole(user.role)),
    refetchInterval: 20000,
  });

  const users = data?.users ?? [];

  const validate = async (id: string, group: "FAMILY" | "GUEST", familyId?: string) => {
    try {
      await apiPatch(`/api/admin/validate-users/${id}`, {
        userGroup: group,
        familyId: group === "FAMILY" ? (familyId ?? null) : null,
      });
      toast.success(t("toast.userValidated"));
      qc.invalidateQueries({ queryKey: ["pending-users"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["families"] });
    } catch (err: any) {
      toast.error(err.message || "Could not validate");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <ShieldCheck className="h-6 w-6 text-violet-600" /> {t("mod.title")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("mod.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {(ownerAdmin || canValidateBookings) && (
            <TabsTrigger value="review" className="shrink-0 gap-1.5">
              <Inbox className="h-4 w-4" />
              {t("mod.tabReview")} ({pendingBookings + users.length})
            </TabsTrigger>
          )}
          {ownerAdmin && (
            <TabsTrigger value="users" className="shrink-0 gap-1.5">
              <UsersIcon className="h-4 w-4" />
              {t("mod.tabUsers")}
            </TabsTrigger>
          )}
          {ownerAdmin && (
            <TabsTrigger value="groups" className="shrink-0 gap-1.5">
              <Users2 className="h-4 w-4" />
              {t("mod.tabGroups")}
            </TabsTrigger>
          )}
          {!ownerAdmin && (
            <TabsTrigger value="invitations" className="shrink-0 gap-1.5">
              <Mail className="h-4 w-4" />
              {t("mod.tabInvitations")}
            </TabsTrigger>
          )}
        </TabsList>

        {(ownerAdmin || canValidateBookings) && (
          <TabsContent value="review" className="mt-4">
            <ReviewPanel
              accounts={users}
              accountsLoading={isLoading}
              onValidate={validate}
              canValidateAccounts={
                ownerAdmin || permissions.includes("users.validate")
              }
            />
          </TabsContent>
        )}
        {ownerAdmin ? (
          <TabsContent value="users" className="mt-4">
            <UsersPanel />
          </TabsContent>
        ) : (
          <TabsContent value="invitations" className="mt-4">
            <InvitationsPanel />
          </TabsContent>
        )}
        {ownerAdmin && (
          <TabsContent value="groups" className="mt-4">
            <GroupsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ---- Users: the role/group-aware account list (admins & owners) ----
function UsersPanel() {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [roleFilter, setRoleFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiGet<{ users: AdminUser[] }>("/api/admin/users"),
  });
  const users = usersData?.users ?? [];
  const familyOptions = Array.from(
    new Set(
      users
        .filter((u) => u.familyName)
        .map((u) => u.familyName as string)
    )
  ).sort();
  const visibleUsers = users.filter(
    (u) =>
      (roleFilter === "all" || u.role === roleFilter) &&
      (groupFilter === "all" ||
        (groupFilter === "unassigned" ? !u.familyId : u.familyName === groupFilter))
  );

  const toggleActive = async (u: AdminUser) => {
    try {
      await apiPatch(`/api/admin/users/${u.id}`, { active: !u.active });
      toast.success(
        u.active ? t("toast.userDisabled") : t("toast.userEnabled")
      );
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.actionFailed"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Home className="h-4 w-4 text-muted-foreground" />
        <Select value={groupFilter} onValueChange={(v) => setGroupFilter(v)}>
          <SelectTrigger className="w-full sm:w-[190px]">
            <SelectValue placeholder={t("admin.allGroups")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.allGroups")}</SelectItem>
            <SelectItem value="unassigned">{t("admin.unassigned")}</SelectItem>
            {familyOptions.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v)}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder={t("admin.allRoles")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.allRoles")}</SelectItem>
            {["USER", "OWNER", "MODERATOR", "ADMIN"].map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ms-auto">
          <Button onClick={() => setInviteOpen(true)} className="gap-2">
            <Mail className="h-4 w-4" /> {t("invitations.inviteGuest")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : visibleUsers.length === 0 ? (
        <div className="grid place-items-center rounded-xl border border-dashed py-12 text-sm text-muted-foreground">
          {t("admin.noUsers")}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin.colName")}</TableHead>
                <TableHead>{t("admin.colUsername")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("admin.colEmail")}</TableHead>
                <TableHead>{t("admin.colRole")}</TableHead>
                <TableHead>{t("admin.colGroup")}</TableHead>
                <TableHead>{t("admin.colStatus")}</TableHead>
                <TableHead className="text-right">{t("admin.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username}
                  </TableCell>
                  <TableCell className="text-muted-foreground">@{u.username}</TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">{u.email}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                        u.role === "ADMIN"
                          ? "border-transparent bg-foreground text-background"
                          : u.role === "OWNER"
                            ? "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
                            : u.role === "MODERATOR"
                              ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300"
                              : "border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400"
                      }`}
                    >
                      {u.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.familyName ?? "\u2014"}
                  </TableCell>
                  <TableCell>
                    {u.active ? (
                      <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                        {t("admin.active")}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-zinc-300 text-zinc-500">
                        {t("admin.disabled")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => toggleActive(u)}
                      title={u.active ? t("admin.disable") : t("admin.enable")}
                    >
                      <Power className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <InviteGuestDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

// ---- Invite-a-guest dialog (compact invitation form) ----
function InviteGuestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", familyId: "" });
  const [lastLink, setLastLink] = useState<{ url: string; email: string } | null>(null);

  const { data: familiesData } = useQuery({
    queryKey: ["families"],
    queryFn: () => apiGet<{ families: { id: string; name: string }[] }>("/api/admin/families"),
    enabled: open,
  });
  const families = familiesData?.families ?? [];

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiPost<{ inviteUrl: string; invitation: { email: string } }>(
        "/api/admin/invitations",
        {
          email: form.email,
          firstName: form.firstName || null,
          lastName: form.lastName || null,
          familyId: form.familyId || null,
        }
      );
      setLastLink({ url: res.inviteUrl, email: res.invitation.email });
      if (navigator.clipboard) navigator.clipboard.writeText(res.inviteUrl).catch(() => {});
      toast.success(t("invitations.sent"));
      setForm({ email: "", firstName: "", lastName: "", familyId: "" });
      qc.invalidateQueries({ queryKey: ["invitations"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("invitations.inviteGuest")}</DialogTitle>
          <DialogDescription>{t("invitations.sendHint")}</DialogDescription>
        </DialogHeader>
        {lastLink ? (
          <div className="grid gap-3">
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
              {t("invitations.linkReady", { email: lastLink.email })}
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={lastLink.url} className="font-mono text-xs" />
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  navigator.clipboard?.writeText(lastLink.url);
                  toast.success(t("invitations.copied"));
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" onClick={() => setLastLink(null)}>
              {t("invitations.inviteAnother")}
            </Button>
          </div>
        ) : (
          <form onSubmit={send} className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>{t("invitations.email")}</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{t("invitations.firstName")}</Label>
                <Input
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("invitations.lastName")}</Label>
                <Input
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("invitations.family")}</Label>
              <Select
                value={form.familyId || "none"}
                onValueChange={(v) => setForm((f) => ({ ...f, familyId: v === "none" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("invitations.noFamily")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("invitations.noFamily")}</SelectItem>
                  {families.map((fam) => (
                    <SelectItem key={fam.id} value={fam.id}>
                      {fam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy} className="gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              <Mail className="h-4 w-4" /> {t("invitations.send")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---- To review: every pending decision on one screen, one click each ----
function ReviewPanel({
  accounts,
  accountsLoading,
  onValidate,
  canValidateAccounts,
}: {
  accounts: PendingValidationUser[];
  accountsLoading: boolean;
  onValidate: (id: string, group: "FAMILY" | "GUEST", familyId?: string) => void;
  canValidateAccounts: boolean;
}) {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [famPick, setFamPick] = useState<Record<string, string>>({});

  const { data: resData, isLoading: resLoading } = useQuery({
    queryKey: ["reservations"],
    queryFn: () => apiGet<{ reservations: Reservation[] }>("/api/reservations"),
    refetchInterval: 20000,
  });
  const pendingRes = (resData?.reservations ?? []).filter(
    (r) => r.status === "PENDING"
  );

  const { data: famData } = useQuery({
    queryKey: ["families"],
    queryFn: () => apiGet<{ families: { id: string; name: string }[] }>("/api/admin/families"),
    enabled: canValidateAccounts && accounts.length > 0,
  });
  const families = famData?.families ?? [];

  const decide = async (id: string, action: "approve" | "reject") => {
    setBusyId(id);
    try {
      await apiPatch(`/api/reservations/${id}`, { action });
      toast.success(
        action === "approve"
          ? t("toast.reservationApproved")
          : t("toast.reservationRejected")
      );
      qc.invalidateQueries({ queryKey: ["reservations"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.actionFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const loading = resLoading || (canValidateAccounts && accountsLoading);
  if (loading) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }
  if (pendingRes.length === 0 && (!canValidateAccounts || accounts.length === 0)) {
    return (
      <div className="grid place-items-center rounded-xl border border-dashed py-16 text-sm text-muted-foreground">
        {t("mod.reviewEmpty")}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {pendingRes.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">
            {t("mod.queueBookings")} ({pendingRes.length})
          </h3>
          {pendingRes.map((r) => (
            <Card key={r.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.product?.title ?? "—"}</span>
                    <Badge
                      variant="outline"
                      className={
                        r.bookingType === "SHARED"
                          ? "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300"
                          : "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
                      }
                    >
                      {r.bookingType}
                    </Badge>
                  </div>
                  <div className="mt-0.5 text-sm text-muted-foreground">
                    {r.guestName} · {formatDate(r.startDate, locale)} →{" "}
                    {formatDate(r.endDate, locale)} · {r.partySize}p
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={busyId === r.id}
                    className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => decide(r.id, "approve")}
                  >
                    <Check className="h-3.5 w-3.5" /> {t("host.approve")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === r.id}
                    className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950"
                    onClick={() => decide(r.id, "reject")}
                  >
                    <X className="h-3.5 w-3.5" /> {t("host.decline")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      {canValidateAccounts && accounts.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">
            {t("mod.queueAccounts")} ({accounts.length})
          </h3>
          {accounts.map((u) => (
            <Card key={u.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="font-medium">
                    {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    @{u.username} · {u.email}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {families.length > 0 && (
                    <Select
                      value={famPick[u.id] ?? families[0].id}
                      onValueChange={(v) => setFamPick((p) => ({ ...p, [u.id]: v }))}
                    >
                      <SelectTrigger className="h-8 w-[170px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {families.map((fam) => (
                          <SelectItem key={fam.id} value={fam.id}>
                            {fam.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={() => onValidate(u.id, "GUEST")}
                  >
                    {t("mod.validateGuest")}
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() =>
                      onValidate(u.id, "FAMILY", famPick[u.id] ?? families[0]?.id)
                    }
                  >
                    <Check className="h-3.5 w-3.5" /> {t("mod.validateFamily")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}
    </div>
  );
}
